<template>
  <div class="aw-root">
    <!-- Password gate -->
    <div v-if="!unlocked" class="aw-gatewrap">
      <form class="aw-gate" @submit.prevent="submit">
        <div class="aw-logo">Ez<b>Fix'd</b></div>
        <p class="aw-gatep">Dealer financing quote — enter the access password to continue.</p>
        <label class="aw-gatelabel" for="aw-key">Password</label>
        <input
          id="aw-key"
          v-model="passwordInput"
          type="password"
          autocomplete="current-password"
          autofocus
        />
        <button type="submit">View</button>
        <div v-if="wrongPassword" class="aw-err">Incorrect password. Try again.</div>
      </form>
    </div>

    <!-- Quote pad -->
    <div v-else class="aw-pad">
      <div class="aw-head">
        <div>
          <h1>Service financing — dealer quote</h1>
          <p>What your customer is offered, and what it costs you.</p>
        </div>
        <div class="aw-docno">EzFix'd</div>
      </div>

      <div class="aw-row">
        <h2>Repair order total</h2>
        <div class="aw-amt">
          <span>$</span>
          <input
            v-model.number="amount"
            type="number"
            min="750"
            max="10000"
            step="50"
            aria-label="Repair order total in dollars"
          />
        </div>
        <input
          class="aw-slider"
          type="range"
          min="750"
          max="10000"
          step="50"
          :value="clampedAmount"
          aria-label="Adjust repair order total"
          @input="amount = Number($event.target.value)"
        />
      </div>

      <div class="aw-row">
        <h2>How it works</h2>
        <p class="aw-copytext">
          Two plans, one cost to you. Both cost
          <strong>9% of the amount financed</strong>, deducted at funding. Customers apply once;
          depending on their credit assessment, someone who applied for the 6-month plan may be
          offered the 12-month plan instead — at the same cost to you.
        </p>
        <p class="aw-copytext aw-last">
          You can advertise both — <em>“0% for 6 months or 6.99% for 12”</em> — or advertise 0%
          only. Either way, a customer not approved for Plan A is offered Plan B on the same
          application, so the advisor always has a payment to quote.
        </p>
      </div>

      <div class="aw-copy">
        <div>
          <h3>What your customer is offered</h3>

          <div class="aw-offer">
            <div class="aw-offer-tag">Plan A · approved for 0%</div>
            <div class="aw-big">
              {{ money(planA.pmt) }}<small> /month × {{ planA.term }}</small>
            </div>
            <div class="aw-rate">0% interest</div>
            <dl>
              <div>
                <dt>Admin fee (financed)</dt>
                <dd>{{ money(adminFee) }}</dd>
              </div>
            </dl>
          </div>

          <div class="aw-offer aw-offer-b">
            <div class="aw-offer-tag">Plan B · not approved for 0%</div>
            <div class="aw-big">
              {{ money(planB.pmt) }}<small> /month × {{ planB.term }}</small>
            </div>
            <div class="aw-rate">6.99% interest</div>
            <dl>
              <div>
                <dt>Admin fee (financed)</dt>
                <dd>{{ money(adminFee) }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div>
          <h3>What you receive — either plan</h3>
          <div class="aw-big">{{ money(netToDealer) }}<small> at funding</small></div>
          <dl>
            <div>
              <dt>Repair order total</dt>
              <dd>{{ money(safeAmount) }}</dd>
            </div>
            <div>
              <dt>Your contribution (9%)</dt>
              <dd>−{{ money(dealerFeeAmount) }}</dd>
            </div>
            <div>
              <dt>Funded within</dt>
              <dd>2 business days</dd>
            </div>
          </dl>
          <p class="aw-note">
            Same amount to you whichever plan the customer is approved for. Nothing to invoice or
            remit.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Client-side gate only — not meant to be strong security, just enough to keep
// the page from casual view (same pattern as partnership.vue). Share the link
// as /applewood?key=<password>, or let the visitor type the password.
const ACCESS_PASSWORD = 'applewood2026'

const ADMIN_FEE = 34.95 // customer admin fee, flat, financed
const DEALER_FEE = 0.09 // dealer's contribution, either plan
const PLAN_A = { term: 6, apr: 0 }
const PLAN_B = { term: 12, apr: 0.0699 }

function monthlyPayment(principal, apr, months) {
  if (apr === 0) return principal / months
  const i = apr / 12
  return (principal * i) / (1 - Math.pow(1 + i, -months))
}

export default {
  name: 'ApplewoodQuote',
  data() {
    return {
      unlocked: false,
      passwordInput: '',
      wrongPassword: false,
      amount: 2000,
      adminFee: ADMIN_FEE,
    }
  },
  computed: {
    safeAmount() {
      return isFinite(this.amount) && this.amount > 0 ? this.amount : 0
    },
    clampedAmount() {
      return Math.min(10000, Math.max(750, this.safeAmount))
    },
    planA() {
      return this.plan(PLAN_A)
    },
    planB() {
      return this.plan(PLAN_B)
    },
    dealerFeeAmount() {
      return this.safeAmount * DEALER_FEE
    },
    netToDealer() {
      return this.safeAmount - this.dealerFeeAmount
    },
  },
  created() {
    const key = this.$route.query.key
    if (key && key === ACCESS_PASSWORD) this.unlocked = true
  },
  methods: {
    submit() {
      if (this.passwordInput === ACCESS_PASSWORD) {
        this.unlocked = true
        this.wrongPassword = false
      } else {
        this.wrongPassword = true
      }
    },
    plan({ term, apr }) {
      const pmt = monthlyPayment(this.safeAmount + ADMIN_FEE, apr, term)
      return { term, pmt }
    },
    money(n) {
      return (
        '$' +
        n.toLocaleString('en-CA', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      )
    },
  },
}
</script>

<style scoped>
.aw-root {
  --paper: #edefe9;
  --field: #fcfcfa;
  --ink: #23302e;
  --ink-soft: #5c6b67;
  --rule: #c3c9bf;
  --rule-strong: #8e988f;
  --stamp: #a8332a;
  --mono: ui-monospace, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  --sans: 'Helvetica Neue', Helvetica, Arial, sans-serif;

  box-sizing: border-box;
  min-height: 100vh;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 16px;
  line-height: 1.45;
  padding: 28px 18px 56px;
  -webkit-font-smoothing: antialiased;
}
.aw-root *,
.aw-root *::before,
.aw-root *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Password gate */
.aw-gatewrap {
  max-width: 360px;
  margin: 6vh auto 0;
}
.aw-gate {
  background: var(--field);
  border: 1px solid var(--rule-strong);
  box-shadow: 2px 2px 0 var(--rule);
  padding: 28px;
}
.aw-logo {
  font-weight: 700;
  font-size: 19px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.aw-logo b {
  color: var(--stamp);
}
.aw-gatep {
  color: var(--ink-soft);
  font-size: 13.5px;
  margin: 8px 0 18px;
}
.aw-gatelabel {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.aw-gate input {
  width: 100%;
  margin: 8px 0 4px;
  padding: 10px 12px;
  border: 1px solid var(--rule-strong);
  font-size: 15px;
  outline: none;
  font-family: var(--sans);
  background: #fff;
}
.aw-gate input:focus {
  border-color: var(--ink);
}
.aw-gate button {
  width: 100%;
  margin-top: 14px;
  padding: 10px;
  border: 1px solid var(--ink);
  background: var(--ink);
  color: var(--field);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--sans);
}
.aw-err {
  color: var(--stamp);
  font-size: 12.5px;
  margin-top: 10px;
}

/* Quote pad */
.aw-pad {
  max-width: 760px;
  margin: 0 auto;
  background: var(--field);
  border: 1px solid var(--rule-strong);
  box-shadow: 2px 2px 0 var(--rule);
}
.aw-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  flex-wrap: wrap;
  padding: 20px 26px 16px;
  border-bottom: 2px solid var(--ink);
}
.aw-head h1 {
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.aw-head p {
  margin-top: 3px;
  font-size: 13px;
  color: var(--ink-soft);
}
.aw-docno {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--ink-soft);
}

.aw-row {
  padding: 18px 26px;
  border-bottom: 1px solid var(--rule);
}
.aw-row h2 {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-soft);
}
.aw-copytext {
  margin-bottom: 8px;
  font-size: 15px;
}
.aw-copytext.aw-last {
  margin-bottom: 0;
}

.aw-amt {
  display: flex;
  align-items: center;
  gap: 10px;
}
.aw-amt span {
  font-family: var(--mono);
  font-size: 30px;
  color: var(--ink-soft);
}
.aw-amt input[type='number'] {
  font-family: var(--mono);
  font-size: 30px;
  font-weight: 600;
  color: var(--ink);
  width: 190px;
  padding: 2px 6px;
  background: transparent;
  border: none;
  border-bottom: 2px solid var(--ink);
  border-radius: 0;
  -moz-appearance: textfield;
}
.aw-amt input[type='number']:focus {
  outline: none;
  border-bottom-color: var(--stamp);
  background: #fff8e9;
}
.aw-amt input::-webkit-outer-spin-button,
.aw-amt input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.aw-slider {
  width: 100%;
  margin-top: 14px;
  accent-color: var(--ink);
}

.aw-copy {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.aw-copy > div {
  padding: 20px 26px 26px;
}
.aw-copy > div:first-child {
  border-right: 1px dashed var(--rule-strong);
}
.aw-copy h3 {
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-soft);
}
.aw-big {
  font-family: var(--mono);
  font-size: 40px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.02em;
}
.aw-big small {
  font-size: 15px;
  font-weight: 400;
  color: var(--ink-soft);
  letter-spacing: 0;
}
.aw-rate {
  color: var(--stamp);
  font-family: var(--mono);
  font-size: 17px;
  margin-top: 8px;
  font-weight: 600;
}
.aw-copy dl {
  margin-top: 16px;
  font-size: 14px;
}
.aw-copy dl div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  border-bottom: 1px solid var(--rule);
}
.aw-copy dl dt {
  color: var(--ink-soft);
}
.aw-copy dl dd {
  font-family: var(--mono);
}

.aw-offer {
  padding: 14px 0 4px;
}
.aw-offer:first-of-type {
  padding-top: 0;
}
.aw-offer .aw-big {
  font-size: 30px;
}
.aw-offer-b {
  margin-top: 18px;
  border-top: 1px solid var(--rule);
}
.aw-offer-tag {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}
.aw-note {
  margin-top: 16px;
  font-size: 13.5px;
  color: var(--ink-soft);
}

@media (max-width: 620px) {
  .aw-root {
    padding: 14px 10px 40px;
  }
  .aw-copy {
    grid-template-columns: 1fr;
  }
  .aw-copy > div:first-child {
    border-right: none;
    border-bottom: 1px dashed var(--rule-strong);
  }
  .aw-amt input[type='number'] {
    width: 140px;
    font-size: 26px;
  }
  .aw-amt span {
    font-size: 26px;
  }
  .aw-big {
    font-size: 34px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/applewood_quote.vue